import React, { useMemo, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Checkbox, Spinner } from "@chakra-ui/react";
import { CleanFiltersIcon } from "~misc/icons";
import Select from "~components/Form/components/Select";
import "./style.scss";
import useGrupoProduto from "~/fetchs/useGrupoProdutoV2";
import useProduto from "~/fetchs/useProdutoV2";
import useFilial from "~/fetchs/useFilialV2";
import useTipoNegociacao from "~/fetchs/useTipoNegociacaoV2";
import { getStoragekeysNegociations } from "~pages/PricesTableV2/helpers/storage";
import { useFormContext, Controller } from "react-hook-form";
import CustomSelectRangePicker from "~components/Form/components/CustomSelectRangePicker";
import { useNavigate, useParams } from "react-router-dom";
import useUnidadeMedida from "~fetchs/useUnidadeMedidaV2";
import { saveLocalStorage } from "~services/utils/SaveIndexBD";
import { initialPath } from "~misc/paths";
import NovaNegociacaoModal from "../Modal/NovaNegociacaoModal";

const FiltroNegociacoesESolicitacoes = ({
  nomeDoFiltro,
  loadingx,
  solicitacao,
  setAplicaServicos,
  showActions = false,
  cliente,
  filtrosDisponiveis,
}) => {
  const [isOpenNegociacaoModal, setIsOpenNegociacaoModal] = useState(false);
  const pickerRef = useRef();
  const { watch, control, reset } = useFormContext();
  const navigate = useNavigate();
  const { id: idCliente } = useParams();

  const watchProduto = watch("produto");
  const watchGrupoProduto = watch("grupoProduto");
  const watchFilial = watch("filial");

  const { unidadesMedidaDisponiveis, grupoMaterialDisponiveis, produtosDisponiveis, filiaisDisponiveis } =
    filtrosDisponiveis || {};

  const optionDefault = useMemo(() => ({ label: "Todos", value: 0 }), []);
  const optionDefaultLoading = useMemo(
    () => ({
      label: loadingx ? "Carregando..." : optionDefault.label,
      value: optionDefault.value,
    }),
    [loadingx, optionDefault],
  );

  const keys = useMemo(() => getStoragekeysNegociations(nomeDoFiltro), [nomeDoFiltro]);

  const { optionGruposProduto } = useGrupoProduto({
    camposOpt: ["nome"],
    gruposDisponiveis: grupoMaterialDisponiveis,
  });

  const { optionProdutos } = useProduto({
    grupoProduto: watchGrupoProduto,
    produtosDisponiveis: produtosDisponiveis,
    produto: watchProduto,
  });

  const { optionFiliais } = useFilial({
    filiaisDisponiveis,
  });

  const { optionNegociacoes } = useTipoNegociacao();

  const optionsOrderDefault = useMemo(
    () => [
      { label: "Todos", value: 1 },
      { label: "Centro", value: "centro" },
      { label: "Grupo de Produto", value: "grupo-produto" },
      { label: "Ordem alfabética", value: "ordem-alfabetica" },
      { label: "Ordem crescente", value: "ordem-crescente" },
      { label: "Ordem decrescente", value: "ordem-decrescente" },
    ],
    [],
  );

  const optionsOrderSolicitations = useMemo(
    () => [
      { label: "Recentes", value: "recentes" },
      { label: "Antigos", value: "antigos" },
    ],
    [],
  );

  const optionsOrder = useMemo(
    () => (solicitacao ? optionsOrderSolicitations : optionsOrderDefault),
    [optionsOrderSolicitations, optionsOrderDefault, solicitacao],
  );

  const optionsStatus = [
    { label: "Todos", value: -1 },
    { label: "Aprovado", value: "aprovado" },
    { label: "Não enviado", value: "nao_enviado" },
    { label: "Pendente", value: "pendente" },
    { label: "Recusado", value: "recusado" },
  ];

  const { optionUnidadesMedida } = useUnidadeMedida({
    unidadesIds: unidadesMedidaDisponiveis,
  });

  const [showDatePicker, setShowDatePicker] = useState(false);

  const watchPeriodo = watch("periodo");

  useEffect(() => {
    if (watchPeriodo?.value === "personalizado") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  }, [watchPeriodo?.value]);

  const clear = () => {
    Object.entries(keys).forEach(([key, value]) => {
      if (key !== "cliente") {
        localStorage.removeItem(value);
      }
    });

    reset({
      unidadeMedida: 0,
      grupoProduto: 0,
      produto: 0,
      filial: 0,
      tipoSolicitacao: 0,
      ordem: solicitacao ? "recentes" : 1,
      statusSolicitacao: -1,
      periodo: { value: 0, label: "Todos", startDate: null, endDate: null },
    });
  };

  const optTipoNegociacao = useMemo(() => [optionDefault, ...optionNegociacoes], [optionDefault, optionNegociacoes]);
  const optGruposProduto = useMemo(
    () => [optionDefaultLoading, ...optionGruposProduto],
    [optionDefaultLoading, optionGruposProduto],
  );

  const optProdutos = useMemo(() => [optionDefaultLoading, ...optionProdutos], [optionDefaultLoading, optionProdutos]);
  const optFiliais = useMemo(() => [optionDefaultLoading, ...optionFiliais], [optionDefaultLoading, optionFiliais]);

  const optUnidadesMedida = useMemo(
    () => [optionDefaultLoading, ...optionUnidadesMedida],
    [optionDefaultLoading, optionUnidadesMedida],
  );

  const handeclickNegociacao = () => {
    if (watchFilial && watchProduto && watchGrupoProduto !== 0) {
      const values = {
        filialId: watchFilial,
        grupoProdutoId: watchGrupoProduto,
        produtoId: watchProduto,
      };
      saveLocalStorage("dados-selecionados-negociacao", values, true);
      navigate(`${initialPath}/tabela-preco/${idCliente}/nova-negociacao`);
    } else {
      setIsOpenNegociacaoModal(true);
    }
  };

  return (
    <>
      <section className="container prices-table-filter" data-test-id="form-preco-cliente">
        <div className="form grid grid-cols-3 gap-x-3">
          {!solicitacao && (
            <Controller
              name="tipoSolicitacao"
              control={control}
              render={({ field }) => (
                <Select
                  label="Tipo de Negociação:"
                  spacingControl={true}
                  options={optTipoNegociacao}
                  onChange={(selectedOption) => {
                    field.onChange(selectedOption.value);
                    localStorage.setItem(keys.negociacao, JSON.stringify(selectedOption));
                  }}
                  value={optTipoNegociacao?.find((option) => option.value === field.value) || 0}
                  data-test-id="select-tipo-negociacao"
                />
              )}
            />
          )}

          <Controller
            name="grupoProduto"
            control={control}
            render={({ field }) => (
              <Select
                label="Grupo de Produto:"
                options={optGruposProduto}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.grupoProduto, JSON.stringify(selectedOption));
                }}
                isDisabled={loadingx}
                loading={loadingx}
                value={optGruposProduto?.find((option) => option.value === field.value) || 0}
                data-test-id="select-grupo-produto"
              />
            )}
          />

          <Controller
            name="produto"
            control={control}
            render={({ field }) => (
              <Select
                label="Produtos:"
                options={optProdutos}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.produto, JSON.stringify(selectedOption));
                }}
                isDisabled={loadingx}
                loading={loadingx}
                value={optProdutos?.find((option) => option.value === field.value) || 0}
                data-test-id="select-produto"
              />
            )}
          />

          <Controller
            name="filial"
            control={control}
            render={({ field }) => (
              <Select
                label="Centro:"
                options={optFiliais}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.filiais, JSON.stringify(selectedOption));
                }}
                isDisabled={loadingx}
                loading={loadingx}
                value={optFiliais?.find((option) => option.value === field.value) || 0}
                data-test-id="select-centro"
              />
            )}
          />

          <Controller
            name="unidadeMedida"
            control={control}
            render={({ field }) => (
              <Select
                label="Embalagem:"
                options={optUnidadesMedida}
                spacingControl={true}
                isDisabled={loadingx}
                loading={loadingx}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.medida, JSON.stringify(selectedOption));
                }}
                value={optUnidadesMedida?.find((option) => option.value === field.value) || optionDefault}
                data-test-id="select-embalagem"
              />
            )}
          />

          {solicitacao && (
            <>
              <Controller
                name="statusSolicitacao"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Status de Solicitação:"
                    options={optionsStatus}
                    spacingControl={true}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption.value);
                      localStorage.setItem(keys.status, JSON.stringify(selectedOption));
                    }}
                    value={optionsStatus?.find((option) => option.value === field.value) || optionsStatus[0]}
                  />
                )}
              />
              <Controller
                name="periodo"
                control={control}
                defaultValue={{ value: 0, label: "Todos" }}
                render={({ field }) => (
                  <CustomSelectRangePicker
                    ref={pickerRef}
                    selectedOption={field.value}
                    showDatePicker={showDatePicker}
                    setShowDatePicker={setShowDatePicker}
                    handleSelectChange={(selectedOption) => {
                      const newValue = {
                        ...selectedOption,
                        startDate: selectedOption.value === "personalizado" ? field.value?.startDate : null,
                        endDate: selectedOption.value === "personalizado" ? field.value?.endDate : null,
                      };
                      field.onChange(newValue);
                      localStorage.setItem(keys.periodo, JSON.stringify(newValue));

                      // Controla a exibição do datepicker
                      if (selectedOption.value === "personalizado") {
                        setShowDatePicker(true);
                      } else {
                        setShowDatePicker(false);
                      }
                    }}
                    onDateChange={(dates) => {
                      const [start, end] = dates;
                      const newValue = {
                        ...field.value,
                        startDate: start,
                        endDate: end,
                      };
                      field.onChange(newValue);
                      localStorage.setItem(keys.periodo, JSON.stringify(newValue));
                    }}
                    startDate={field.value?.startDate}
                    endDate={field.value?.endDate}
                    clearSelectRange={() => {
                      const defaultValue = { value: 0, label: "Todos", startDate: null, endDate: null };
                      field.onChange(defaultValue);
                      setShowDatePicker(false);
                      localStorage.setItem(keys.periodo, JSON.stringify(defaultValue));
                    }}
                  />
                )}
              />
            </>
          )}

          {showActions && (
            <>
              <div className="nova-negociacao-link">
                <button
                  type="button"
                  onClick={() => handeclickNegociacao()}
                  className="nova-negociacao-button"
                  data-test-id="button-nova-negociacao"
                >
                  Nova Negociação
                </button>
              </div>
              <div className="checkbox-container">
                <label className="checkbox">
                  <Checkbox
                    onChange={(e) => setAplicaServicos((prev) => ({ ...prev, fracionamento: e.target.checked }))}
                    data-test-id="checkbox-incluir-fracionamento"
                  />
                  <span className="desc">Incluir Valor de Fracionamento</span>
                </label>
              </div>

              <div className="checkbox-container">
                <label className="checkbox">
                  <Checkbox
                    onChange={(e) => setAplicaServicos((prev) => ({ ...prev, descarga: e.target.checked }))}
                    data-test-id="checkbox-incluir-descarga"
                  />
                  <span className="desc">Incluir Valor de Descarga Manual</span>
                </label>
              </div>
            </>
          )}

          <div className="col-span-3 flex ml-auto gap-4">
            <button type="button" className="clear-filter cursor-pointer" onClick={clear}>
              <span className="desc ml-0 mr-2 cursor-pointer">Limpar Filtros</span>
              <CleanFiltersIcon />
            </button>
            <div className="ml-auto">
              <Controller
                name="ordem"
                control={control}
                render={({ field }) => (
                  <Select
                    className="no-margin"
                    label="Ordenar por:"
                    variation="compact-inline"
                    options={optionsOrder}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption.value);
                    }}
                    value={optionsOrder.find((option) => option.value === field.value) || optionsOrder[0]}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="flex">
          {loadingx && (
            <p className="flex items-start gap-2 text-sm mt-4">
              <Spinner size={"sm"} />
              Verificando se há atualizações nas requisições
            </p>
          )}
        </div>
      </div>

      <NovaNegociacaoModal isOpen={isOpenNegociacaoModal} onClose={setIsOpenNegociacaoModal} cliente={cliente} />
    </>
  );
};

FiltroNegociacoesESolicitacoes.propTypes = {
  nomeDoFiltro: PropTypes.string.isRequired,
  loadingx: PropTypes.bool,
  solicitacao: PropTypes.bool,
  setAplicaServicos: PropTypes.func,
  showActions: PropTypes.bool,
  cliente: PropTypes.object,
  filtrosDisponiveis: PropTypes.shape({
    unidadesMedidaDisponiveis: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    grupoMaterialDisponiveis: PropTypes.arrayOf(PropTypes.object),
    produtosDisponiveis: PropTypes.arrayOf(PropTypes.object),
    filiaisDisponiveis: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default FiltroNegociacoesESolicitacoes;
